/**
 * mountWidget — единая точка рендера. Используется И рантаймом (apps/widget),
 * И превью (apps/admin) — одна реализация.
 *
 * Создаёт Shadow DOM, инжектит RESET → css ВЫБРАННОГО ШАБЛОНА → переменные
 * пользователя (--inbx-*) → его custom CSS, строит «хром» (лончер/панель/оверлей/
 * teaser) и вызывает template.build для скелета. Шаблон владеет своим видом
 * полностью; переменные накладываются поверх.
 */
import type { ProductCard, RuntimeWidgetConfig } from '@inbx/shared/schemas';

import { el } from './dom';
import { getTemplate } from './templates';
import type { BuildHandlers } from './templates/types';

export type WidgetHandlers = BuildHandlers;

export interface MountOptions {
  host: HTMLElement;
  config: RuntimeWidgetConfig;
  handlers?: WidgetHandlers;
  /** popup/modal: показать раскрытым сразу (превью «развёрнут»). */
  openInitially?: boolean;
  /** Превью: сразу показать экран «успешно отправлено». */
  initialSuccess?: boolean;
  /** Режим превью в админке: «хром» позиционируется внутри стейджа (absolute). */
  preview?: boolean;
  /** Состояние превью (collapsed/expanded/notification/success/offline). */
  state?: string;
  /** Демо-данные (примеры сообщений) — только превью. */
  demo?: boolean;
  /** URL общего CSS виджетов (RESET + стили шаблонов) — грузится <link>-ом
   *  внутрь Shadow DOM. Per-workspace bundle.css в проде / общий в превью. */
  cssHref?: string;
  /** Вызывается при открытии (рантайм: подгрузка истории чата). */
  onOpen?: () => void;
}

export interface WidgetController {
  open(): void;
  close(): void;
  destroy(): void;
  showTeaser(text: string): void;
  appendChatMessage(
    text: string,
    direction?: 'in' | 'out',
    avatarUrl?: string,
    products?: ProductCard[],
  ): void;
}

export function mountWidget(o: MountOptions): WidgetController {
  const { host, config } = o;
  const mode = config.behavior.mode ?? 'popup';
  const template = getTemplate(config.templateId, config.type);

  const shadow = host.shadowRoot ?? host.attachShadow({ mode: 'open' });
  shadow.innerHTML = '';
  host.setAttribute('data-inbx-widget', '');
  host.setAttribute('data-type', config.type);
  host.setAttribute('data-template', config.templateId);
  host.setAttribute('data-mode', mode);
  host.setAttribute('data-position', config.behavior.position ?? 'bottom-right');
  if (o.preview) host.setAttribute('data-preview', '');
  // Переменные пользователя — поверх css шаблона.
  for (const [k, v] of Object.entries(config.vars ?? {})) host.style.setProperty(k, v);

  // Общий CSS (RESET + стили шаблонов) — <link> внутрь Shadow DOM. Не в js.
  if (o.cssHref) {
    const link = el('link');
    link.rel = 'stylesheet';
    link.href = o.cssHref;
    shadow.appendChild(link);
  }
  // Свой CSS клиента — поверх, последним.
  if (config.customCssUrl) {
    const link = el('link');
    link.rel = 'stylesheet';
    link.href = config.customCssUrl;
    shadow.appendChild(link);
  }

  const panel = el('div', 'w-panel');
  const body = el('div', 'w-body');
  panel.appendChild(body);

  let fab: HTMLButtonElement | null = null;
  let overlay: HTMLDivElement | null = null;
  let teaser: HTMLDivElement | null = null;

  const open = (): void => {
    if (mode === 'modal' && overlay) overlay.classList.add('is-open');
    else panel.classList.add('is-open');
    fab?.classList.add('is-hidden');
    o.onOpen?.();
  };
  const close = (): void => {
    overlay?.classList.remove('is-open');
    panel.classList.remove('is-open');
    fab?.classList.remove('is-hidden');
  };

  const isPromo = config.type === 'promo';

  if (mode === 'inline') {
    panel.classList.add('is-open');
    shadow.appendChild(panel);
  } else if (mode === 'modal') {
    overlay = el('div', 'w-overlay');
    overlay.appendChild(panel);
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) close();
    });
    shadow.appendChild(overlay);
    if (config.behavior.dismissible !== false) addClose(panel, close);
    if (o.openInitially || isPromo) open();
  } else if (isPromo) {
    shadow.appendChild(panel);
    panel.classList.add('is-open');
  } else {
    fab = el('button', 'w-fab');
    fab.type = 'button';
    // Лончер: чат — иконка-круг (через css); форма/опрос — кнопка-пилюля с подписью
    // (как в лид-формах), а не чат-круг.
    if (config.type !== 'chat') {
      const cc = (config.content ?? {}) as { launcher_text?: string; title?: string };
      fab.textContent =
        cc.launcher_text || cc.title || (config.type === 'survey' ? 'Пройти опрос' : 'Оставить заявку');
    }
    fab.addEventListener('click', () => (panel.classList.contains('is-open') ? close() : open()));
    teaser = el('div', 'w-teaser');
    teaser.appendChild(el('div', 'w-teaser-text'));
    teaser.addEventListener('click', () => {
      teaser?.classList.remove('is-visible');
      open();
    });
    shadow.append(teaser, fab, panel);
    if (config.behavior.dismissible !== false) addClose(panel, close);
    if (o.openInitially) open();
  }

  const instance = template
    ? template.build({
        root: body,
        config,
        handlers: o.handlers ?? {},
        initialSuccess: o.initialSuccess,
        state: o.state,
        demo: o.demo,
      })
    : null;

  const showTeaser = (text: string): void => {
    if (!teaser) return;
    const t = teaser.querySelector('.w-teaser-text');
    if (t) t.textContent = text;
    teaser.classList.add('is-visible');
  };
  if (config.type === 'chat' && o.state === 'notification') {
    const c = (config.content ?? {}) as { greeting?: string; teaser_text?: string };
    showTeaser(c.teaser_text ?? 'Напишите нам, мы онлайн!');
  }

  return {
    open,
    close,
    showTeaser,
    appendChatMessage: (text, direction = 'in', avatarUrl, products) =>
      instance?.appendMessage?.(text, direction, avatarUrl, products),
    destroy: () => {
      shadow.innerHTML = '';
      host.removeAttribute('data-inbx-widget');
    },
  };
}

function addClose(panel: HTMLElement, close: () => void): void {
  const btn = el('button', 'w-close');
  btn.type = 'button';
  btn.addEventListener('click', close);
  panel.appendChild(btn);
}
